import { HeaderNav, MobileNav } from '../Nav';
import { Logo } from '../Logo';
import { MainSponsorLogo } from '../MainSponsorLogo';
import { PwaBackButton } from '../PwaBackButton';
import { useIsWideDesktop } from '@ui/utils/useDeviceType';
import { CrossFade } from '@ui/common/components/CrossFade';
import { useTenantConfigQuery } from '@data/tenants';

interface HeaderProps {
  environment?: 'TEST' | 'DEV';
}

// SFL's header is a flat, no-dots variant that stacks the nav in its own
// band below the brand row instead of sharing the wide-desktop row — a
// tenant-specific exception to the shared Header E layout, not a toggle.
const FLAT_STACKED_TENANT_ID = 'sfl';

function Header({ environment }: HeaderProps) {
  const isCompact = !useIsWideDesktop();
  const { data: tenantConfig } = useTenantConfigQuery();
  const isFlatStacked = tenantConfig?.tenantId === FLAT_STACKED_TENANT_ID;

  // isFlatStacked is folded in so CrossFade treats SFL's tenant-config-driven
  // layout swap (dotted shared row -> flat stacked row) as an animated
  // variant change instead of a same-key DOM mutation on tenant config resolve.
  const variantKey = `${isCompact ? 'compact' : 'wide'}-${isFlatStacked}`;
  const headerClassName = isFlatStacked && !isCompact ? 'header header--stacked' : 'header';

  return (
    <CrossFade variantKey={variantKey} className="header-cross-fade">
      <header className={headerClassName}>
        {/* Skip link is rendered for every variant so keyboard users on
            mobile/tablet (Bluetooth keyboards, screen-reader rotor) can
            bypass the logos + nav. WCAG 2.4.1, Level A. */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        {renderHeaderContent(isCompact, isFlatStacked)}
        {environment && <span className="header__env-badge text-3xs">{environment}</span>}
      </header>
    </CrossFade>
  );
}

function renderHeaderContent(isCompact: boolean, isFlatStacked: boolean) {
  if (isCompact) {
    return (
      <>
        <div className="header__brand">
          {!isFlatStacked && <div className="header__bg" aria-hidden="true" />}
          <Logo />
          <MainSponsorLogo />
        </div>
        <MobileNav className="header__nav" isFlatDrawer={isFlatStacked} />
      </>
    );
  }

  if (isFlatStacked) {
    return (
      <>
        <div className="header__top">
          <PwaBackButton />
          <Logo />
          <div className="header__sponsor-lockup">
            <MainSponsorLogo />
          </div>
        </div>
        <HeaderNav className="header__nav header__nav--band" />
      </>
    );
  }

  return (
    <>
      <div className="header__bg" aria-hidden="true" />
      <PwaBackButton />
      <Logo />
      <HeaderNav className="header__nav" />
      <div className="header__sponsor-lockup">
        <MainSponsorLogo />
      </div>
    </>
  );
}

export default Header;
